import {Box} from '@mantine/core';
import Image from 'next/image';
import React from 'react';
import type {Recruitment} from '../../../../../core/repositories/recruitingInfoRepository';
import {cn} from '../../../../../lib/classname';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {ScheduleButtonNewDesign} from '../../../../recruitment/components/ScheduleButton';
import {Deadline} from './Deadline';
import {Schedules} from './Schedules';
import {Tags} from './Tags';

type Props = {
  recruitment: Recruitment;
  className?: string;
};

export const CardForSp: React.FC<Props> = ({recruitment, className}) => {
  const recruitingInfoPage = `/recruiting_info/${recruitment.category === 'archive' ? 'archives' : 'view'}/${recruitment.detailID}`;

  const openCompanyPage = (id: number) => {
    if (id === 0) {
      return;
    }

    window.open(`/company/${id}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box className={cn('w-full bg-white p-16', className)}>
      <div className="mb-24 [&:nth-last-child(1)]:mb-0 pb-24 [&:nth-last-child(1)]:pb-0 border-b-[1px] border-line border-collapse [&:nth-last-child(1)]:border-b-0">
        <div className="flex flex-col">
          <div className="flex w-full">
            {recruitment.category === 'client' &&
              recruitment.companies[0]?.logoURLs?.['140x105'] && (
                <Image
                  className="h-[62px] w-[90px] bg-white object-contain relative cursor-pointer mb-4"
                  src={recruitment.companies[0]?.logoURLs?.['140x105']}
                  alt={recruitment.title}
                  width={62}
                  height={90}
                  onClick={() => {
                    openCompanyPage(recruitment.companies[0]?.id ?? 0);
                  }}
                />
              )}
            <Box className="ml-auto mb-4 flex items-center gap-8">
              {recruitment.schedules.length > 0 && recruitment.schedules[0] && (
                <>
                  <Deadline schedule={recruitment.schedules[0]} />
                  <ScheduleButtonNewDesign
                    scheduleId={recruitment.schedules[0].id}
                    isRegisteredInitial={
                      recruitment.schedules[0].isCalendarRegistered
                    }
                    registeredCountInitial={
                      recruitment.schedules[0].calendarRegisteredCount
                    }
                    buttonStyle="icon"
                  />
                </>
              )}
            </Box>
          </div>
          <div className="justify-start items-center inline-flex">
            <div className={'flex-col justify-start gap-2 inline-flex'}>
              <div className="">
                <CommonLink
                  href={recruitingInfoPage}
                  data-gtm-click-recruitment-detail-button="true"
                >
                  <Box className="text-md text-black-1 font-bold line-clamp-2 hover:opacity-40">
                    {recruitment.title}
                  </Box>
                </CommonLink>
                <p
                  className="text-base text-black-1 mt-4 hover:opacity-40 line-clamp-2"
                  onClick={() => {
                    openCompanyPage(recruitment.companies[0]?.id ?? 0);
                  }}
                >
                  {recruitment.companies.map((company, i) => (
                    <React.Fragment key={company.id}>
                      {i > 0 && ', '}
                      <span>{company.name}</span>
                    </React.Fragment>
                  ))}
                </p>
              </div>
            </div>
          </div>
        </div>
        <Box className="py-8">
          <Tags
            tags={recruitment.tags}
            targetYear={recruitment.targetYear}
            type={recruitment.type}
            groupedJobTypes={recruitment.groupedJobTypes}
          />
        </Box>
        {recruitment.schedules.length > 0 && recruitment.schedules[0] && (
          <Box className="py-4">
            <Schedules schedules={[recruitment.schedules[0]]} />
          </Box>
        )}
        <Box className="mt-[5px] flex justify-between items-start w-full">
          <Box>
            {recruitment.schedules.length > 1 && (
              <CommonLink href={recruitingInfoPage}>
                <Box className="text-base text-black-1 whitespace-nowrap cursor-pointer hover:opacity-50 pl-12">
                  ほか{recruitment.schedules.length - 1}日程
                </Box>
              </CommonLink>
            )}
          </Box>
          <Box className="flex gap-8">
            <CommonLink
              href={recruitingInfoPage}
              data-gtm-click-recruitment-detail-button="true"
            >
              <Box className="px-16 py-8 bg-line text-black text-sm font-bold rounded-[8px] shadow-card hover:opacity-80">
                募集詳細
              </Box>
            </CommonLink>
            {recruitment.entryURL &&
              ['medium', 'large'].includes(
                recruitment.companies[0]?.pageType ?? '',
              ) && (
                <a
                  href={recruitment.entryURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  id="recruiting_info_entry_button"
                  className="text-sm"
                >
                  <button className="px-16 py-8 bg-gradient-to-r from-error to-sub font-bold text-white rounded-[8px] shadow-card hover:opacity-80">
                    エントリー
                  </button>
                </a>
              )}
          </Box>
        </Box>
      </div>
    </Box>
  );
};
